import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import { getUserAchievements, removeAchievement } from '../../../services/api';
import { getBadgeUrl } from '../../../utils/badgeUrl';

function AchievementRow({ ach, idx, onRemove }) {
    return (
        <div className={`w-full border-b border-black ${idx % 2 === 0 ? 'bg-black/5' : 'bg-black/10'}`}>
            <div className="flex gap-3 w-full">
                <div className="relative w-[80px] bg-offwhite flex items-center justify-center border-r border-black/20 px-2 py-2 group">
                    <img
                        src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                        alt={ach.badgeID}
                        className="shrink-0"
                        style={{ imageRendering: 'pixelated', maxWidth: 'none', maxHeight: 'none' }}
                        loading="lazy"
                    />
                    <div className="hidden group-hover:block absolute left-[50px] top-0 ml-2 z-50">
                        <div className="bg-offwhite border border-black p-2">
                            <div style={{ width: 'calc(var(--bw) * 3)', height: 'calc(var(--bh) * 3)' }}>
                                <img
                                    src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                                    alt={`${ach.badgeID} preview`}
                                    onLoad={(e) => {
                                        const img = e.currentTarget;
                                        img.parentElement.style.setProperty('--bw', `${img.naturalWidth || img.width}px`);
                                        img.parentElement.style.setProperty('--bh', `${img.naturalHeight || img.height}px`);
                                    }}
                                    style={{ imageRendering: 'pixelated', transform: 'scale(3)', transformOrigin: 'top left', maxWidth: 'none', maxHeight: 'none' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="min-w-0 flex-1 py-2 pr-2">
                    <div className="flex items-start justify-between gap-2">
                        <div className="text-coal font-['Jersey_10'] text-xl leading-none truncate" title={ach.name}>{ach.name}</div>
                        <button
                            type="button"
                            onClick={() => onRemove(ach)}
                            className="shrink-0 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1 bg-maroon text-white hover:bg-red-900 transition-colors rounded"
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                        >
                            Remove
                        </button>
                    </div>
                    <div className="mt-1 text-coal/90 text-xs truncate" title={ach.description}>{ach.description}</div>
                    {ach.dateAchieved && (
                        <div className="mt-1 text-[11px] text-coal/60">
                            Earned {new Date(ach.dateAchieved).toLocaleDateString()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function ConfirmModal({ ach, habboName, onConfirm, onCancel, submitting }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
            <div className="relative bg-offwhite border border-black shadow-xl rounded p-5 w-full max-w-sm mx-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white border border-black/20 p-2 flex items-center justify-center">
                        <div style={{ width: 'calc(var(--bw2) * 3)', height: 'calc(var(--bh2) * 3)' }}>
                            <img
                                src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                                alt={ach.badgeID}
                                onLoad={(e) => {
                                    const img = e.currentTarget;
                                    img.parentElement.style.setProperty('--bw2', `${img.naturalWidth || img.width}px`);
                                    img.parentElement.style.setProperty('--bh2', `${img.naturalHeight || img.height}px`);
                                }}
                                style={{ imageRendering: 'pixelated', transform: 'scale(3)', transformOrigin: 'top left', maxWidth: 'none', maxHeight: 'none' }}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="font-['Jersey_10'] text-xl leading-none">{ach.name}</div>
                        <div className="text-xs text-coal/70 mt-0.5">{ach.description}</div>
                    </div>
                </div>
                <p className="text-sm text-coal mb-4">
                    Are you sure you want to remove <span className="font-bold">{ach.name}</span> from <span className="font-bold">{habboName}</span>? This cannot be undone.
                </p>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={submitting}
                        className="flex-1 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        {submitting ? 'Removing...' : 'Confirm'}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        className="flex-1 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

function RemoveAchievements({ userData }) {
    const [habboName, setHabboName] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [achievements, setAchievements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [confirmAch, setConfirmAch] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const handleSearch = async (e) => {
        e.preventDefault();
        const name = searchInput.trim();
        if (!name) return;

        setLoading(true);
        setSearched(false);
        setAchievements([]);
        setHabboName('');

        try {
            const data = await getUserAchievements(name);
            if (data?.success) {
                setHabboName(name);
                setAchievements(data.achievements || []);
                setSearched(true);
            } else {
                toast.error(data?.message || 'User not found');
            }
        } catch {
            toast.error('Failed to load user achievements');
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async () => {
        if (!confirmAch) return;
        setSubmitting(true);
        try {
            const data = await removeAchievement(habboName, confirmAch.badgeID);
            if (data?.success) {
                toast.success(`Removed "${confirmAch.name}" from ${habboName}`);
                setAchievements(prev => prev.filter(a => a.badgeID !== confirmAch.badgeID));
                setConfirmAch(null);
            } else {
                toast.error(data?.message || 'Failed to remove achievement');
            }
        } catch {
            toast.error('Failed to remove achievement');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AdminLayout userData={userData}>
            <style>{`.habbo-scroll::-webkit-scrollbar{width:16px;height:16px}.habbo-scroll::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb:hover{background:#7a7a7a}`}</style>

            <ContentBox theme="default" tint="red" header="Remove Achievements">
                <form onSubmit={handleSearch} className="flex gap-2 mb-0">
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Enter Habbo username"
                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                        disabled={loading}
                    />
                    <button
                        type="submit"
                        disabled={loading || !searchInput.trim()}
                        className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        {loading ? 'Loading...' : 'Search'}
                    </button>
                </form>
            </ContentBox>

            {searched && (
                <div className="mt-4">
                    <ContentBox theme="default" tint="red" header={`Achievements: ${habboName}`} padding="p-0">
                        {achievements.length === 0 ? (
                            <div className="p-3 text-coal/70 text-sm">This user has no achievements.</div>
                        ) : (
                            <div className="habbo-scroll overflow-y-auto flex flex-col" style={{ maxHeight: 400 }}>
                                {achievements.map((ach, idx) => (
                                    <AchievementRow
                                        key={ach.badgeID}
                                        ach={ach}
                                        idx={idx}
                                        onRemove={setConfirmAch}
                                    />
                                ))}
                            </div>
                        )}
                    </ContentBox>
                </div>
            )}

            {confirmAch && (
                <ConfirmModal
                    ach={confirmAch}
                    habboName={habboName}
                    onConfirm={handleRemove}
                    onCancel={() => setConfirmAch(null)}
                    submitting={submitting}
                />
            )}
        </AdminLayout>
    );
}

export default RemoveAchievements;
